import { Button } from '@material-ui/core';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import addRawDataFile from '../actions/admin/addRawDataFile';
import addReport from '../actions/admin/addReport';
import useStore from '../store/store';
import Dialog from './Dialog';

interface UploadTestFilesDialogProps {
    kitId: number;
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UploadTestFilesDialog = ({
    kitId,
    open,
    onClose,
    onSubmit,
}: UploadTestFilesDialogProps) => {
    const [reportFile, setReportFile] = React.useState<File | null>(null);
    const [rawDataFiles, setRawDataFiles] = React.useState<File[]>([]);
    const isLoading = useStore((state) => state.isLoading);

    const handleReportFileChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            if (!uploadedFile.name.endsWith('.pdf')) {
                alert('Please upload a valid PDF file');
                return;
            }
            setReportFile(uploadedFile);
        }
    };

    const handleRawDataFileChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const uploadedFiles = event.target.files;
        if (uploadedFiles) {
            setRawDataFiles(Array.from(uploadedFiles));
        }
    };

    const handleClose = () => {
        if (isLoading) {
            return;
        }
        setReportFile(null);
        setRawDataFiles([]);
        onClose();
    };

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (reportFile) {
            await addReport({
                kitId,
                releaseDate: new Date(),
                report: reportFile,
            });
        }
        await Promise.all(
            rawDataFiles.map((rawDataFile) =>
                addRawDataFile({
                    kitId,
                    releaseDate: new Date(),
                    rawDataFile,
                })
            )
        );

        onSubmit();
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={'Upload Test Files'}
            onSubmit={handleSubmit}
            submitDisabled={
                (!reportFile && rawDataFiles.length === 0) || isLoading
            }
        >
            <Button
                color="primary"
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                style={{
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    borderRadius: '25px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontSize: '16px',
                }}
            >
                Upload Report
                <VisuallyHiddenInput
                    type="file"
                    accept=".pdf"
                    onChange={handleReportFileChange}
                />
            </Button>
            <p>{reportFile?.name}</p>
            <Button
                color="primary"
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                style={{
                    marginTop: '1rem',
                    marginBottom: '1rem',
                    borderRadius: '25px',
                    boxShadow: 'none',
                    textTransform: 'none',
                    fontSize: '16px',
                }}
            >
                Upload Raw Data
                <VisuallyHiddenInput
                    type="file"
                    onChange={handleRawDataFileChange}
                    multiple
                />
            </Button>
            {rawDataFiles.map((rawDataFile) => (
                <p key={rawDataFile.name}>{rawDataFile.name}</p>
            ))}
        </Dialog>
    );
};

export default UploadTestFilesDialog;
